import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { User as UserIcon, Mail, Phone, MapPin, Hash, Cake, Sparkles, Smile, Save, Lock, Heart, Stethoscope, Users as UsersIcon } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import AvatarStudioCore from "@/components/AvatarStudioCore";

export default function ClientProfilePage() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    full_name: "", nickname: "", ndis_number: "", email: "",
    phone: "", address: "", date_of_birth: "", bio: "",
    emergency_contact_name: "", emergency_contact_phone: "",
    emergency_contact_relationship: "", emergency_medical_info: "",
  });
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/client/profile"); setForm((p) => ({ ...p, ...data })); }
    catch (e) { toast.error(formatApiError(e)); }
  })(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const {
        full_name, nickname, ndis_number, phone, address, date_of_birth, bio,
        emergency_contact_name, emergency_contact_phone,
        emergency_contact_relationship, emergency_medical_info,
      } = form;
      const { data } = await api.put("/client/profile", {
        full_name, nickname, ndis_number, phone, address, date_of_birth, bio,
        emergency_contact_name, emergency_contact_phone,
        emergency_contact_relationship, emergency_medical_info,
      });
      setForm((p) => ({ ...p, ...data }));
      await refresh();
      toast.success("Profile saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 max-w-4xl" data-testid="profile-page">
      <header>
        <h1 className="text-4xl font-display font-black tracking-tight">My Profile</h1>
        <p className="text-muted-foreground">Your personal details — used on cover letters, PDFs, and across the app.</p>
      </header>

      {/* Hero card with avatar + identity */}
      <Card className="rounded-3xl p-6 sm:p-8 border-2 relative overflow-hidden card-lift" data-testid="profile-hero">
        <div className="blob animate-blob" style={{ width: 240, height: 240, top: -80, right: -60, background: "hsl(var(--client) / 0.20)" }} />
        <div className="blob animate-blob" style={{ width: 200, height: 200, bottom: -80, left: -40, background: "hsl(var(--coral) / 0.18)", animationDelay: "-7s" }} />
        <div className="relative z-10 flex flex-wrap items-center gap-5">
          <div className="relative">
            <div className="w-32 h-32 rounded-3xl overflow-hidden ring-4 ring-client/30 bg-muted grid place-items-center" data-testid="profile-avatar">
              {user?.avatar_url
                ? <img src={user.avatar_url} alt="me" className="w-full h-full object-cover" />
                : <span className="text-5xl font-display font-black text-client">{(form.full_name || "?")[0]}</span>}
            </div>
            <Button onClick={() => setAvatarOpen(true)} size="sm" data-testid="change-avatar-btn"
              className="absolute -bottom-2 -right-2 rounded-full h-9 px-3 shadow-lg gap-1 font-bold text-white"
              style={{ background: "linear-gradient(135deg, hsl(var(--coral)), hsl(var(--sun)))" }}>
              <Sparkles className="w-3.5 h-3.5" /> Change
            </Button>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground font-bold">NDIS participant</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight" data-testid="profile-name">
              {form.full_name || "Add your name below"}
            </h2>
            {form.nickname && <p className="text-coral font-bold text-lg mt-0.5" data-testid="profile-nickname">"{form.nickname}"</p>}
            <div className="flex flex-wrap gap-2 mt-3 text-sm">
              {form.ndis_number && <span className="rounded-full px-3 py-1 bg-client/10 text-client font-bold border-2 border-client/20" data-testid="profile-ndis-pill"><Hash className="w-3 h-3 inline mr-1" /> NDIS {form.ndis_number}</span>}
              {form.email && <span className="rounded-full px-3 py-1 bg-muted font-semibold" data-testid="profile-email-pill"><Mail className="w-3 h-3 inline mr-1" /> {form.email}</span>}
            </div>
          </div>
        </div>
      </Card>

      {/* Form */}
      <Card className="rounded-3xl p-6 border-2 space-y-5" data-testid="profile-form-card">
        <h2 className="font-display font-extrabold text-2xl flex items-center gap-2"><UserIcon className="w-5 h-5 text-client" /> Personal details</h2>

        <div className="grid sm:grid-cols-2 gap-4">
          <FieldWithIcon icon={UserIcon} id="full_name" label="Name & Surname" required v={form.full_name} oc={upd("full_name")} placeholder="e.g. Sarah Thompson" />
          <FieldWithIcon icon={Smile} id="nickname" label="Nickname" v={form.nickname} oc={upd("nickname")} placeholder="What friends call you" />
          <FieldWithIcon icon={Hash} id="ndis_number" label="NDIS Number" v={form.ndis_number} oc={upd("ndis_number")} placeholder="e.g. 430123456" />
          <FieldWithIcon icon={Cake} id="date_of_birth" label="Date of Birth" type="date" v={form.date_of_birth} oc={upd("date_of_birth")} />
          <FieldWithIcon icon={Mail} id="email" label="Email (sign-in)" v={form.email} disabled lock placeholder="—" />
          <FieldWithIcon icon={Phone} id="phone" label="Phone" v={form.phone} oc={upd("phone")} placeholder="e.g. 0400 111 222" />
          <div className="sm:col-span-2">
            <FieldWithIcon icon={MapPin} id="address" label="Address" v={form.address} oc={upd("address")} placeholder="Street, suburb, state, postcode" />
          </div>
          <div className="sm:col-span-2 space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold">A little about me (optional)</Label>
            <Textarea value={form.bio || ""} onChange={upd("bio")} rows={3} className="rounded-xl"
              placeholder="A few words your support workers can read — what makes you smile, what you'd like them to know."
              data-testid="profile-bio" />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 justify-between items-center pt-2 border-t">
          <p className="text-xs text-muted-foreground">Your name and NDIS number appear on every invoice cover letter and report.</p>
          <Button onClick={save} disabled={busy} className="rounded-full font-bold gap-2 min-h-[48px]" style={{ background: "hsl(var(--client))" }} data-testid="save-profile-btn">
            <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save profile"}
          </Button>
        </div>
      </Card>

      {/* Emergency contact + medical info — also visible to support workers */}
      <Card className="rounded-3xl p-6 border-2 space-y-5" data-testid="emergency-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display font-extrabold text-2xl flex items-center gap-2">
              <Heart className="w-5 h-5 text-coral" /> Emergency contact & medical info
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Visible to your support workers and printed on incident PDFs. Keep it current.
            </p>
          </div>
          <span className="rounded-full px-3 py-1 bg-coral/10 text-coral text-xs font-bold border-2 border-coral/20">
            <UsersIcon className="w-3 h-3 inline mr-1" /> Workers can see this
          </span>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <FieldWithIcon icon={UserIcon} id="emergency_contact_name" label="Emergency contact name"
            v={form.emergency_contact_name} oc={upd("emergency_contact_name")} placeholder="e.g. Jane Thompson" />
          <FieldWithIcon icon={Phone} id="emergency_contact_phone" label="Emergency contact phone"
            v={form.emergency_contact_phone} oc={upd("emergency_contact_phone")} placeholder="e.g. 0411 222 333" />
          <FieldWithIcon icon={UsersIcon} id="emergency_contact_relationship" label="Relationship"
            v={form.emergency_contact_relationship} oc={upd("emergency_contact_relationship")} placeholder="Mother, partner, friend…" />
          <div className="sm:col-span-2 space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold flex items-center gap-1.5">
              <Stethoscope className="w-3.5 h-3.5 text-coral" /> Medical info / allergies / conditions
            </Label>
            <Textarea
              value={form.emergency_medical_info || ""}
              onChange={upd("emergency_medical_info")}
              rows={3}
              className="rounded-xl"
              placeholder="e.g. Allergic to penicillin, type 2 diabetic, takes Metformin…"
              data-testid="profile-field-emergency_medical_info"
            />
          </div>
        </div>

        <div className="flex justify-end pt-2 border-t">
          <Button onClick={save} disabled={busy} className="rounded-full font-bold gap-2 min-h-[48px]"
            style={{ background: "hsl(var(--coral))" }} data-testid="save-emergency-btn">
            <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save emergency info"}
          </Button>
        </div>
      </Card>

      {/* Avatar modal */}
      <Dialog open={avatarOpen} onOpenChange={setAvatarOpen}>
        <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="avatar-modal">
          <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Sparkles className="w-5 h-5 text-coral" /> Change my avatar</DialogTitle></DialogHeader>
          <AvatarStudioCore
            endpoints={{ put: "/client/avatar", aiGenerate: "/client/avatar/ai-generate", aiEdit: "/client/avatar/ai-edit" }}
            currentUrl={user?.avatar_url || ""}
            seedName={form.nickname || form.full_name || "Friend"}
            onChange={() => refresh()}
            compact
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FieldWithIcon({ icon: Icon, id, label, v, oc, type = "text", placeholder, disabled, lock, required }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs uppercase tracking-wider font-bold flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5 text-client" /> {label} {required && <span className="text-coral">*</span>}
        {lock && <Lock className="w-3 h-3 text-muted-foreground ml-auto" />}
      </Label>
      <Input id={id} value={v || ""} onChange={oc} type={type} placeholder={placeholder} disabled={disabled}
        className={`min-h-[48px] rounded-xl ${disabled ? "bg-muted/50" : ""}`}
        data-testid={`profile-field-${id}`} />
    </div>
  );
}
